import {
  LayoutDashboard,
  Search,
  Users,
  Mic,
  Calendar,
  Lightbulb,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { isEarlyLifecycleProjectStatus } from "@shared/realtime-types";

export interface ProjectTab {
  id: string;
  label: string;
  icon: LucideIcon;
}

export const PROJECT_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "research", label: "Research", icon: Search },
  { id: "participants", label: "Participants", icon: Users },
  { id: "interviews", label: "Interviews", icon: Mic },
  { id: "recommendations", label: "Recommendations", icon: Sparkles },
  { id: "meetings", label: "Meetings", icon: Calendar },
  { id: "insights", label: "Insights", icon: Lightbulb },
] as const;

export type ProjectTabId = (typeof PROJECT_TABS)[number]["id"];

const PROJECT_TAB_ID_SET = new Set<ProjectTabId>(
  PROJECT_TABS.map((tab) => tab.id),
);

export function isProjectTabId(value: string): value is ProjectTabId {
  return PROJECT_TAB_ID_SET.has(value as ProjectTabId);
}

/**
 * Tabs hidden while a project is still in early lifecycle (`created` /
 * `scoping` / `seeding`). Those workspace surfaces have no data until scoping
 * seeds the project, so we don't surface a dead pill. Research specifically
 * stays hidden until spec 040 (research pipelines) lands (plan 041 §R11).
 */
export const EARLY_LIFECYCLE_HIDDEN_TAB_IDS = new Set<ProjectTabId>(["research"]);

/**
 * The project tabs visible for a given `projects.status`. Early-lifecycle
 * projects hide {@link EARLY_LIFECYCLE_HIDDEN_TAB_IDS}; everything else shows
 * the full strip. Single source of truth for tab visibility so the pill strip
 * (ProjectTabs) and the route gate (RequireActiveProject) stay in step.
 */
export function visibleProjectTabsForStatus(
  status: string | null | undefined,
): readonly (typeof PROJECT_TABS)[number][] {
  if (isEarlyLifecycleProjectStatus(status)) {
    return PROJECT_TABS.filter((tab) => !EARLY_LIFECYCLE_HIDDEN_TAB_IDS.has(tab.id));
  }
  return PROJECT_TABS;
}

/** True when `tabId` is hidden for an early-lifecycle project of `status`. */
export function isProjectTabHiddenForStatus(
  tabId: ProjectTabId,
  status: string | null | undefined,
): boolean {
  return (
    isEarlyLifecycleProjectStatus(status) && EARLY_LIFECYCLE_HIDDEN_TAB_IDS.has(tabId)
  );
}
